/**
 * Mock Data for Flow Visualization Testing
 *
 * Sample data to test the rendering of the flow visualization components.
 * Based on the example data from docs/mockups/flows-final-design2.html
 */

import type { InflowNode, AccountNode, OutflowNode, FlowEdge } from './types';
import { COLORS } from './constants';

/**
 * Mock inflow nodes (income sources)
 */
export const mockInflowNodes: InflowNode[] = [
  {
    id: 'income-1',
    type: 'income',
    name: 'Net Salary',
    value: 8500,
    color: COLORS.income,
    categoryId: 'cat-salary',
  },
  {
    id: 'income-2',
    type: 'income',
    name: '401k (Payroll)',
    value: 1500,
    color: COLORS.income,
    categoryId: 'cat-401k',
  },
  {
    id: 'income-3',
    type: 'income',
    name: 'Employer Match',
    value: 500,
    color: COLORS.income,
    categoryId: 'cat-match',
  },
  {
    id: 'income-4',
    type: 'income',
    name: 'Dividends',
    value: 200,
    color: COLORS.income,
    categoryId: 'cat-dividends',
  },
  {
    id: 'income-5',
    type: 'income',
    name: 'Rental Income',
    value: 1500,
    color: COLORS.income,
    categoryId: 'cat-rental',
  },
];

/**
 * Mock account nodes
 */
export const mockAccountNodes: AccountNode[] = [
  // Tier A - Operating
  {
    id: 'account-1',
    type: 'account',
    name: 'Checking',
    balance: 8500,
    flowTotal: 12000,
    tier: 'A',
    accountType: 'depository',
    accountId: 'acc-checking',
  },
  {
    id: 'account-2',
    type: 'account',
    name: 'Chase CC',
    balance: -2400,
    flowTotal: 3200,
    tier: 'A',
    accountType: 'credit',
    accountId: 'acc-chase',
  },
  {
    id: 'account-3',
    type: 'account',
    name: 'Amex CC',
    balance: -800,
    flowTotal: 530,
    tier: 'A',
    accountType: 'credit',
    accountId: 'acc-amex',
  },

  // Tier B - Savings & Investments
  {
    id: 'account-4',
    type: 'account',
    name: 'Savings',
    balance: 15000,
    flowTotal: 500,
    tier: 'B',
    accountType: 'depository',
    accountId: 'acc-savings',
  },
  {
    id: 'account-5',
    type: 'account',
    name: '401k',
    balance: 125000,
    flowTotal: 2000,
    tier: 'B',
    accountType: 'investment',
    accountId: 'acc-401k',
  },
  {
    id: 'account-6',
    type: 'account',
    name: 'Roth IRA',
    balance: 45000,
    flowTotal: 0,
    tier: 'B',
    accountType: 'investment',
    accountId: 'acc-roth',
  },
  {
    id: 'account-7',
    type: 'account',
    name: 'Brokerage',
    balance: 32000,
    flowTotal: 200,
    tier: 'B',
    accountType: 'investment',
    accountId: 'acc-brokerage',
  },
  {
    id: 'account-8',
    type: 'account',
    name: 'HSA',
    balance: 20000,
    flowTotal: 0,
    tier: 'B',
    accountType: 'investment',
    accountId: 'acc-hsa',
  },

  // Tier C - Other Assets
  {
    id: 'account-9',
    type: 'account',
    name: 'Rental Property',
    balance: 280000,
    flowTotal: 1500,
    tier: 'C',
    accountType: 'other',
    accountId: 'acc-rental-prop',
  },
  {
    id: 'account-10',
    type: 'account',
    name: 'Annuity',
    balance: 50000,
    flowTotal: 0,
    tier: 'C',
    accountType: 'other',
    accountId: 'acc-annuity',
  },
];

/**
 * Mock outflow nodes (expense categories)
 */
export const mockOutflowNodes: OutflowNode[] = [
  {
    id: 'expense-1',
    type: 'expense',
    name: 'Rent',
    value: 2200,
    color: COLORS.expense,
    categoryId: 'cat-rent',
  },
  {
    id: 'expense-2',
    type: 'expense',
    name: 'Utilities',
    value: 350,
    color: COLORS.expense,
    categoryId: 'cat-utilities',
  },
  {
    id: 'expense-3',
    type: 'expense',
    name: 'Groceries',
    value: 600,
    color: COLORS.expense,
    categoryId: 'cat-groceries',
  },
  {
    id: 'expense-4',
    type: 'expense',
    name: 'Restaurants',
    value: 450,
    color: COLORS.expense,
    categoryId: 'cat-restaurants',
  },
  {
    id: 'expense-5',
    type: 'expense',
    name: 'Shopping',
    value: 400,
    color: COLORS.expense,
    categoryId: 'cat-shopping',
  },
  {
    id: 'expense-6',
    type: 'expense',
    name: 'Gas',
    value: 200,
    color: COLORS.expense,
    categoryId: 'cat-gas',
  },
  {
    id: 'expense-7',
    type: 'expense',
    name: 'Subscriptions',
    value: 150,
    color: COLORS.expense,
    categoryId: 'cat-subscriptions',
  },
  {
    id: 'expense-8',
    type: 'expense',
    name: 'Travel',
    value: 350,
    color: COLORS.expense,
    categoryId: 'cat-travel',
  },
  {
    id: 'expense-9',
    type: 'expense',
    name: 'Entertainment',
    value: 180,
    color: COLORS.expense,
    categoryId: 'cat-entertainment',
  },
  {
    id: 'expense-10',
    type: 'expense',
    name: 'Property Expenses',
    value: 1450,
    color: COLORS.expense,
    categoryId: 'cat-property',
  },
];

/**
 * Mock edges connecting nodes
 */
export const mockEdges: FlowEdge[] = [
  // Income -> Accounts
  {
    id: 'e1',
    source: 'income-1',
    target: 'account-1',
    value: 8500,
    type: 'income',
  },
  {
    id: 'e2',
    source: 'income-2',
    target: 'account-5', // 401k -> 401k account (will be lumped)
    value: 1500,
    type: 'income',
  },
  {
    id: 'e3',
    source: 'income-3',
    target: 'account-5', // Employer match -> 401k
    value: 500,
    type: 'income',
  },
  {
    id: 'e4',
    source: 'income-4',
    target: 'account-7', // Dividends -> Brokerage
    value: 200,
    type: 'income',
  },
  {
    id: 'e5',
    source: 'income-5',
    target: 'account-9', // Rental income -> Rental property
    value: 1500,
    type: 'income',
  },

  // Accounts -> Expenses
  {
    id: 'e6',
    source: 'account-1',
    target: 'expense-1',
    value: 2200,
    type: 'expense',
  },
  {
    id: 'e7',
    source: 'account-1',
    target: 'expense-2',
    value: 350,
    type: 'expense',
  },
  {
    id: 'e8',
    source: 'account-1',
    target: 'expense-3',
    value: 250,
    type: 'expense',
  },
  {
    id: 'e9',
    source: 'account-2',
    target: 'expense-3',
    value: 350,
    type: 'expense',
  },
  {
    id: 'e10',
    source: 'account-2',
    target: 'expense-4',
    value: 450,
    type: 'expense',
  },
  {
    id: 'e11',
    source: 'account-2',
    target: 'expense-5',
    value: 400,
    type: 'expense',
  },
  {
    id: 'e12',
    source: 'account-2',
    target: 'expense-6',
    value: 200,
    type: 'expense',
  },
  {
    id: 'e13',
    source: 'account-2',
    target: 'expense-7',
    value: 150,
    type: 'expense',
  },
  {
    id: 'e14',
    source: 'account-3',
    target: 'expense-8',
    value: 350,
    type: 'expense',
  },
  {
    id: 'e15',
    source: 'account-3',
    target: 'expense-9',
    value: 180,
    type: 'expense',
  },
  {
    id: 'e16',
    source: 'account-9', // Rental property -> Property expenses
    target: 'expense-10',
    value: 1450,
    type: 'expense',
  },

  // Transfers (within accounts)
  {
    id: 'e17',
    source: 'account-1',
    target: 'account-4', // Checking -> Savings
    value: 500,
    type: 'transfer',
  },
];

/**
 * Summary data for the mock period
 */
export const mockSummary = {
  totalIncome: 12200,
  totalExpenses: 6330,
  toSavingsInvest: 2700,
  netToChecking: 3170,
  savingsRate: 48,
};
